import type {
  BeneficiaryDocumentsByFiscalQuotaResponse,
  BeneficiaryPortfolioResponse,
  DocumentBlock,
  DocumentUpdateRequest,
  DocumentWithReviewResponse,
  ReviewBlock,
} from "@/domain/documentBlocks";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { isObject, listSlot, type PageShape } from "../cache/cacheSlots";
import { defineEntityCache } from "../cache/entityCache";

export interface BeneficiaryDocumentPatch {
  document?: Partial<DocumentBlock>;
  review?: Partial<ReviewBlock>;
}

const idOf = (record: DocumentWithReviewResponse) => record.document.uuid;

const merge = (
  record: DocumentWithReviewResponse,
  patch: BeneficiaryDocumentPatch,
): DocumentWithReviewResponse => ({
  ...record,
  document: { ...record.document, ...patch.document },
  review: { ...record.review, ...patch.review },
});

/** Documents grouped by quota: records keep their group, removals shrink its total. */
const byQuotaPage: PageShape<
  BeneficiaryDocumentsByFiscalQuotaResponse,
  DocumentWithReviewResponse,
  string
> = {
  isPage: (value): value is BeneficiaryDocumentsByFiscalQuotaResponse =>
    isObject(value) && Array.isArray(value.groups),
  records: (page) => page.groups.flatMap((group) => group.documents),
  withRecords: (page, records) => {
    const kept = new Map(records.map((record) => [idOf(record), record]));
    return {
      ...page,
      groups: page.groups.map((group) => {
        const documents = group.documents.flatMap((record) => kept.get(idOf(record)) ?? []);
        return {
          ...group,
          documents,
          total: group.total - (group.documents.length - documents.length),
        };
      }),
    };
  },
  idOf,
};

const portfolioPage: PageShape<BeneficiaryPortfolioResponse, DocumentWithReviewResponse, string> = {
  isPage: (value): value is BeneficiaryPortfolioResponse =>
    isObject(value) && Array.isArray(value.owned) && typeof value.total_count === "number",
  records: (page) => page.owned,
  withRecords: (page, owned, countDelta) => ({
    ...page,
    owned,
    total_count: page.total_count + countDelta,
  }),
  idOf,
};

export const beneficiaryDocumentCache = defineEntityCache<string, BeneficiaryDocumentPatch>({
  entity: InvalidationEntity.BENEFICIARY_DOCUMENT,
  slots: (orgId) => [
    listSlot({
      prefix: queryKeys.beneficiaries.documentsByQuotaAll(orgId),
      shape: byQuotaPage,
      merge,
    }),
    listSlot({ prefix: queryKeys.beneficiaries.documentsAll(orgId), shape: portfolioPage, merge }),
  ],
  records: () => [],
});

/** The fields of a document edit the row can show before the server answers. */
export function toDocumentPatch(data: DocumentUpdateRequest): BeneficiaryDocumentPatch {
  return {
    document: {
      ...(data.title !== undefined ? { title: data.title } : {}),
      ...(data.doc_type !== undefined ? { doc_type: data.doc_type } : {}),
      ...(data.share_with_investors != null
        ? { shared_with_investors: data.share_with_investors }
        : {}),
    },
  };
}
